import { colors } from "shared/theme/color";
import { formatCurrency } from "shared/utils";
import { PackageAnalyticsRow } from "shared/types";

interface PackageCompareRevenueChartProps {
  rows: PackageAnalyticsRow[];
}

// Local geometry, not shared with chartUtils.ts — that file's constants are
// tuned for the VERTICAL band charts elsewhere (bottom-axis month labels,
// numeric-tick left axis). This chart is horizontal on purpose (see below),
// so its row/label/plot math is unrelated; niceMax/formatShort aren't used
// either, since there's no tick axis to round numbers for.
const VBOX_W = 520;
const LABEL_W = 220;
// Reserved space for the value text so it always sits AFTER the bar, the
// same way for every row — without this, the row with the largest value
// (always close to the max-width bar, by construction) would be the one
// row whose label has to move inside the bar to avoid clipping, which
// looks like inconsistent styling rather than the deliberate overflow
// guard it actually is. 100px fits "Rp 999.999.999" comfortably.
const VALUE_ZONE_W = 100;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 8;
const ROW_H = 42;
// Extra row height for a name that needs a second label line.
const LINE_EXTRA = 14;
const BAR_H = 22;
const PLOT_W = VBOX_W - LABEL_W - VALUE_ZONE_W - PAD_RIGHT;
// Fits LABEL_W - 8 at fontSize 11 (~11 * 0.62 px/char, the same estimate
// ratio used elsewhere in this file family) with some slack.
const LABEL_MAX_CHARS_PER_LINE = 28;

// Wraps a name onto up to 2 lines at a word boundary instead of a single
// fixed-length truncation — package/membership names run long (up to 43+
// chars, e.g. "Unlimited Private and Duet Class (1 Month)") and often share
// a prefix ("Unlimited…" repeated across rows), so single-line truncation
// either collides across rows or needs a hover fallback to read in full.
// Wrapping keeps every name fully readable directly on the chart. Only a
// name that still doesn't fit 2 lines (not expected for this app's real
// catalog data) falls back to an ellipsis on the second line.
const wrapLabel = (name: string): string[] => {
  const words = name.split(" ");
  const lines: string[] = [];
  let current = "";
  let i = 0;
  while (i < words.length && lines.length < 2) {
    const candidate = current ? `${current} ${words[i]}` : words[i];
    if (candidate.length <= LABEL_MAX_CHARS_PER_LINE || !current) {
      current = candidate;
      i++;
    } else {
      lines.push(current);
      current = "";
    }
  }
  if (current) lines.push(current);
  const hasMore = i < words.length;
  const lastIdx = lines.length - 1;
  if (hasMore || (lines[lastIdx]?.length ?? 0) > LABEL_MAX_CHARS_PER_LINE) {
    const base =
      lines[lastIdx].length > LABEL_MAX_CHARS_PER_LINE - 1
        ? lines[lastIdx].slice(0, LABEL_MAX_CHARS_PER_LINE - 1)
        : lines[lastIdx];
    lines[lastIdx] = `${base}…`;
  }
  return lines;
};

// Single-series bar chart of "Penjualan" per selected item — the Bandingkan
// report's own comparison, visualized. Horizontal, not vertical: with up to
// 8 selected items and names as long as "Unlimited Private and Duet Class
// (1 Month)" that often share a common prefix ("Unlimited…" repeated across
// several rows), a vertical chart's bottom-axis labels either collide under
// any fixed-width truncation or need a separate index/legend lookup.
// Horizontal removes the problem instead of working around it — each label
// sits directly beside its own bar, wrapped onto up to 2 lines so the full
// name is always visible without hovering — and the exact value is printed
// at the bar's end so every row reads at a glance.
//
// Caller (PackageComparisonContent) caps `rows` to a manageable count
// before passing it in; this component just renders whatever it's given
// and never treats an all-zero selection as "empty" — a flat set of bars
// is a legitimate comparison result here.
const PackageCompareRevenueChart = ({ rows }: PackageCompareRevenueChartProps) => {
  // Rows whose name needs a second line get extra height — a plain i*ROW_H
  // grid would either clip a wrapped name or waste space under a short one.
  const rowHeights = rows.map((r) => (wrapLabel(r.name).length > 1 ? ROW_H + LINE_EXTRA : ROW_H));
  const rowTops: number[] = [];
  rowHeights.reduce((top, h) => {
    rowTops.push(top);
    return top + h;
  }, PAD_TOP);
  const vboxH = rowHeights.reduce((sum, h) => sum + h, 0) + PAD_TOP + PAD_BOTTOM;
  // Raw max with headroom, not a "nice" rounded ceiling — there's no tick
  // axis to round for, so rounding would only waste plot width.
  const max = Math.max(...rows.map((r) => r.sales), 1) * 1.08;

  const bars = rows.map((r, i) => {
    const rowH = rowHeights[i];
    const rowY = rowTops[i] + rowH / 2;
    const barW = (r.sales / max) * PLOT_W;
    return { ...r, rowH, rowY, barW, lines: wrapLabel(r.name) };
  });

  return (
    <svg
      width="100%"
      height={vboxH}
      viewBox={`0 0 ${VBOX_W} ${vboxH}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Where every bar starts — a light visual anchor standing in for
          the axis line a numeric-tick axis would otherwise need. */}
      <line
        x1={LABEL_W}
        x2={LABEL_W}
        y1={PAD_TOP}
        y2={vboxH - PAD_BOTTOM}
        stroke={colors.line}
        strokeWidth={1}
      />

      {bars.map((b) => {
        const oneLine = b.lines.length === 1;
        return (
          <g key={b.package_id}>
            {b.lines.map((line, li) => (
              <text
                key={li}
                x={LABEL_W - 8}
                y={oneLine ? b.rowY + 4 : b.rowY + (li === 0 ? -5 : 8)}
                textAnchor="end"
                fontSize={11}
                fill={colors.ink2}
              >
                {line}
              </text>
            ))}
            <rect
              x={LABEL_W}
              y={b.rowY - BAR_H / 2}
              width={Math.max(0, b.barW)}
              height={BAR_H}
              fill={colors.navy}
            />
            <text
              x={LABEL_W + b.barW + 8}
              y={b.rowY + 4}
              textAnchor="start"
              fontSize={11}
              fontWeight={600}
              fill={colors.ink}
            >
              {formatCurrency(b.sales)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default PackageCompareRevenueChart;
